import cn from 'clsx'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import BodyText from '../Typography/BodyText'
import Caption from '../Typography/Caption'
import CheckboxButton from './CheckboxButton'

const CheckboxWrapper = styled.label`
  display: flex;
  position: relative;

  &[hidden] {
    display: none;
  }

  &.variant-large {
    gap: ${rem(16)};
  }

  &.variant-small {
    gap: ${rem(8)};
  }

  >:nth-child(2) {
    flex-grow: 1;
  }
`

const TextElement = styled.span`
  display: flex;
  flex-direction: column;
`

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Determines which visual representation of the checkbox will be shown */
  variant?: 'large' | 'small';
  /** A possible 3rd value for check boxes, this is "sort of checked". */
  indeterminate?: boolean;
  /**
   * This is text that will appear below the main children with a different visual
   * look. Often used as additional descriptions to the main element.
   **/
  subText?: string;
}

/**
 * @deprecated Use `<CheckboxInput />` instead.
 */
const SquareCheckboxInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    children,
    className,
    id,
    variant = 'small',
    hidden,
    subText,
    ...inputProps
  } = props

  const hasChildren = React.Children.toArray(children).length > 0

  return (
    <CheckboxWrapper
      hidden={hidden}
      className={cn(
        className,
        `variant-${variant}`,
      )}
      htmlFor={id}
    >
      <CheckboxButton
        {...inputProps}
        size={variant}
        ref={ref}
        id={id}
      />
      <TextElement>
        {hasChildren && variant === 'large' && <BodyText variant="large" as="span">{children}</BodyText>}
        {subText && variant === 'large' && <BodyText variant="medium" colour="neutral-two" as="span">{subText}</BodyText>}

        {hasChildren && variant === 'small' && <Caption variant="large" as="span">{children}</Caption>}
        {subText && variant === 'small' && <Caption variant="medium" colour="neutral-three" as="span">{subText}</Caption>}
      </TextElement>
    </CheckboxWrapper>
  )
})

SquareCheckboxInput.displayName = 'SquareCheckboxInput'

export default SquareCheckboxInput
